import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { AuthResponse, ApiError } from '../types';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Token management
let accessToken: string | null = null;
let refreshToken: string | null = null;
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

export function setTokens(at: string, rt: string) {
  accessToken = at;
  refreshToken = rt;
  localStorage.setItem('accessToken', at);
  localStorage.setItem('refreshToken', rt);
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem('accessToken');
  }
  return accessToken;
}

export function getRefreshToken(): string | null {
  if (!refreshToken) {
    refreshToken = localStorage.getItem('refreshToken');
  }
  return refreshToken;
}

// Initialize tokens from localStorage
if (typeof window !== 'undefined') {
  accessToken = localStorage.getItem('accessToken');
  refreshToken = localStorage.getItem('refreshToken');
}

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers!.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch((err) => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const rt = getRefreshToken();
        if (!rt) throw new Error('No refresh token');
        
        const response = await axios.post<AuthResponse>(`${API_BASE}/auth/refresh`, { refreshToken: rt });
        const { accessToken: newAt, refreshToken: newRt } = response.data;
        
        setTokens(newAt, newRt);
        
        // Process queued requests
        failedQueue.forEach(({ resolve }) => resolve(newAt));
        failedQueue = [];
        
        originalRequest.headers!.Authorization = `Bearer ${newAt}`;
        return api(originalRequest);
      } catch (refreshError) {
        failedQueue.forEach(({ reject }) => reject(refreshError as Error));
        failedQueue = [];
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data: { email: string; password: string; name?: string }) =>
    api.post<AuthResponse>('/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post<AuthResponse>('/auth/login', data),
  refresh: (refreshToken: string) =>
    api.post<AuthResponse>('/auth/refresh', { refreshToken }),
  me: () => api.get<{ user: AuthResponse['user'] }>('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Videos API
export const videosApi = {
  list: (params?: { page?: number; limit?: number; category?: string; search?: string; sort?: string }) =>
    api.get<{ videos: any[]; pagination: any }>('/videos', { params }),
  get: (slug: string) => api.get<{ video: any }>(`/videos/${slug}`),
  getById: (id: string) => api.get<{ video: any }>(`/videos/admin/${id}`),
  create: (data: FormData) => api.post('/videos/upload', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id: string, data: any) => api.patch(`/videos/admin/${id}`, data),
  delete: (id: string) => api.delete(`/videos/admin/${id}`),
  categories: () => api.get<{ categories: any[] }>('/videos/categories/list'),
  createCategory: (data: { name: string; description?: string }) =>
    api.post('/videos/categories', data),
};

// Streaming API
export const streamApi = {
  hlsMaster: (videoId: string) => `/stream/hls/${videoId}/master.m3u8`,
  hlsQuality: (videoId: string, quality: string) => `/stream/hls/${videoId}/${quality}/playlist.m3u8`,
  hlsSegment: (videoId: string, quality: string, segment: string) => `/stream/hls/${videoId}/${quality}/${segment}`,
  mp4: (videoId: string, quality: string) => `/stream/mp4/${videoId}/${quality}.mp4`,
  image: (type: 'thumbnail' | 'poster', videoId: string) => `/stream/image/${type}/${videoId}`,
};

// User API
export const userApi = {
  history: () => api.get<{ history: any[] }>('/user/history'),
  progress: (videoId: string, data: { progress: number; completed?: boolean }) =>
    api.post(`/user/progress/${videoId}`, data),
  getProgress: (videoId: string) => api.get<{ progress: number; completed: boolean }>(`/user/progress/${videoId}`),
  favorites: () => api.get<{ favorites: any[] }>('/user/favorites'),
  addFavorite: (videoId: string) => api.post(`/user/favorites/${videoId}`),
  removeFavorite: (videoId: string) => api.delete(`/user/favorites/${videoId}`),
  checkFavorite: (videoId: string) => api.get<{ isFavorite: boolean }>(`/user/favorites/${videoId}/check`),
};

export default api;